/**
 * «Доступи» користувача (ч.1073, вимога власника: «після запрошення —
 * редагувати доступ: додати інші мої організації або прибрати одну»).
 *
 * Показує всі організації акаунта з роллю користувача в кожній; «зберегти»
 * замінює набір повністю (PUT /memberships/{id}/access). Для ще не
 * прийнятого запрошення доступи запрацюють після прийняття.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getMemberAccess, setMemberAccess } from '@/api/invitations';
import { orgs } from '@/api/catalogs';
import { accessRoles } from '@/api/admin';
import { extractApiError } from '@/utils/extractApiError';
import OrgAccessTable, { toGrants } from './OrgAccessTable';

export default function MemberAccessDialog({ membershipId, email, pending, onClose }: {
  membershipId: string;
  email: string;
  /** Запрошення ще не прийняте. */
  pending: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const orgsQ = useQuery({ queryKey: ['orgs-list'], queryFn: () => orgs.list({ limit: 200 }) });
  const rolesQ = useQuery({ queryKey: ['access-roles'], queryFn: () => accessRoles.list() });
  const accessQ = useQuery({
    queryKey: ['member-access', membershipId],
    queryFn: () => getMemberAccess(membershipId),
  });
  const [value, setValue] = useState<Record<string, string> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (accessQ.data && value === null) {
      setValue(Object.fromEntries(
        accessQ.data.grants.map((g) => [g.organization_id, g.access_role_id])));
    }
  }, [accessQ.data, value]);

  const save = useMutation({
    // Лише організації, показані у таблиці: набір замінюється повністю, тож
    // «невидимий» рядок не має ані додаватись, ані мовчки зникати.
    mutationFn: () => {
      const shown = new Set((orgsQ.data?.items ?? []).map((o) => o.id));
      const grants = toGrants(value ?? {}).filter((g) => shown.has(g.organization_id));
      return setMemberAccess(membershipId, grants);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['memberships'] });
      qc.invalidateQueries({ queryKey: ['invitations'] });
      qc.invalidateQueries({ queryKey: ['member-access', membershipId] });
      onClose();
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const orgsList = orgsQ.data?.items ?? [];
  const loading = orgsQ.isLoading || rolesQ.isLoading || accessQ.isLoading || value === null;

  return (
    <>
      <div className="backdrop" onClick={onClose} />
      <div className="drawer" style={{ width: 'min(640px, 100vw)' }} role="dialog"
        aria-label={`Доступи: ${email}`}>
        <h2>Доступи: {email}</h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: 0 }}>
          Оберіть роль у кожній організації, до якої має бути доступ; «без
          доступу» — організацію прибрано.
          {pending && ' Запрошення ще не прийняте — доступи запрацюють, щойно користувач його прийме.'}
        </p>
        {loading ? <div>Завантаження…</div> : orgsList.length === 0 ? (
          <div style={{ color: 'var(--text-secondary)' }}>В акаунті ще немає організацій.</div>
        ) : (
          <OrgAccessTable orgs={orgsList} roles={rolesQ.data ?? []}
            value={value ?? {}} onChange={setValue} />
        )}
        {error && <div className="error">{error}</div>}
        <div className="drawer__actions">
          <button type="button" onClick={onClose}>скасувати</button>
          <button type="button" className="primary" disabled={loading || save.isPending}
            onClick={() => { setError(null); save.mutate(); }}>
            {save.isPending ? 'збереження…' : 'зберегти'}
          </button>
        </div>
      </div>
    </>
  );
}
